import { Type } from "@angular/core";
import { NotificationTemplate } from "src/app/core/models/notification.model";
import { NotificationActionsRendererComponent } from "./notification-actions-renderer/notification-actions-renderer.component";
import { NotificationImageRendererComponent } from "./notification-image-renderer/notification-image-renderer.component";
import { NotificationLinkRendererComponent } from "./notification-link-renderer/notification-link-renderer.component";
import { NotificationSurveyRendererComponent } from "./notification-survey-renderer/notification-survey-renderer.component";
import { NotificationTemplateRenderer } from "./notification-template-renderer";
import { NotificationTextRendererComponent } from "./notification-text-renderer/notification-text-renderer.component";
import { NotificationVideoRendererComponent } from "./notification-video-renderer/notification-video-renderer.component";

/**
 * Registry mapping each `NotificationTemplate.kind` to the standalone
 * renderer component that draws it (issue #416's "registry of typed
 * template components" requirement). `NotificationItemComponent` looks up
 * this map via {@link resolveNotificationTemplateRenderer} and mounts the
 * result with `NgComponentOutlet` + `ngComponentOutletInputs`.
 *
 * Every entry implements {@link NotificationTemplateRenderer} (`template`
 * input, `respond` output), so the outlet binding stays generic — adding a
 * new kind only means adding a component here and a case in the union in
 * `notification.model.ts`.
 */
export const NOTIFICATION_TEMPLATE_RENDERERS: Record<string, Type<NotificationTemplateRenderer>> = {
  text: NotificationTextRendererComponent,
  link: NotificationLinkRendererComponent,
  image: NotificationImageRendererComponent,
  video: NotificationVideoRendererComponent,
  actions: NotificationActionsRendererComponent,
  survey: NotificationSurveyRendererComponent,
};

/**
 * Resolves the renderer for a template, falling back to
 * {@link NotificationTextRendererComponent} for any kind not present in
 * {@link NOTIFICATION_TEMPLATE_RENDERERS} — the "unknown template kinds
 * degrade to the text renderer" rule from the issue's template registry
 * design.
 */
export function resolveNotificationTemplateRenderer(
  template: NotificationTemplate
): Type<NotificationTemplateRenderer> {
  return NOTIFICATION_TEMPLATE_RENDERERS[template?.kind] ?? NotificationTextRendererComponent;
}
